import { AccountHub, useLogin } from '@bnnvara/accounts';
import { TrackEvent } from '@bnnvara/analytics';
import { Hub as BaseHub } from '@bnnvara/hub';
import { Logo } from '@bnnvara/icons/src/components/BrandIcons/Logo';
import { ArrowDown } from '@bnnvara/icons/src/components/UserInterfaceIcons/ArrowDown';
import { ArrowUp } from '@bnnvara/icons/src/components/UserInterfaceIcons/ArrowUp';
import { Close } from '@bnnvara/icons/src/components/UserInterfaceIcons/Close';
import { MemberHeart } from '@bnnvara/icons/src/components/UserInterfaceIcons/MemberHeart';
import { Search } from '@bnnvara/icons/src/components/UserInterfaceIcons/Search';
import uniqueId from 'lodash/uniqueId';
import NextLink from 'next/link';
import Router, { useRouter } from 'next/router';
import React, { useEffect } from 'react';

import useSharedRef from '../../hooks/useSharedRef';
import useUI, {
  TYPE_CLOSE_SEARCH,
  TYPE_TOGGLE_ACCOUNT_HUB,
  TYPE_TOGGLE_MENU,
  TYPE_TOGGLE_SEARCH,
} from '../../hooks/useUI';
import useWindowScrollEffect from '../../hooks/useWindowScrollEffect';
import Link from '../Link';
import NotificationsHub from './NotificationsHub';
import * as S from './styled';

const SearchIcon = () => {
  const {
    state: { searchOpen },
    dispatch,
  } = useUI();

  return (
    <S.SearchButton onClick={() => dispatch({ type: TYPE_TOGGLE_SEARCH })}>
      {searchOpen ? (
        <Close size="100%" />
      ) : (
        <Search width="100%" height="100%" />
      )}
    </S.SearchButton>
  );
};

const MenuButton = () => {
  const {
    state: { menuOpen },
    dispatch,
  } = useUI();

  const menuLabel = menuOpen ? 'Menu sluiten' : 'Menu openen';

  return (
    <S.MenuButton
      variant="text"
      onClick={() => {
        TrackEvent('Hub', 'click', menuLabel);
        dispatch({ type: TYPE_TOGGLE_MENU });
      }}
    >
      Menu {menuOpen ? <ArrowUp /> : <ArrowDown />}
    </S.MenuButton>
  );
};

const Hub = () => {
  const router = useRouter();
  const { createRef, refs } = useSharedRef();
  const { isLoggedIn } = useLogin();

  const {
    state: { searchOpen, accountHubOpen },
    dispatch,
  } = useUI();

  const closeHandler = () => dispatch({ type: TYPE_TOGGLE_ACCOUNT_HUB });

  useEffect(() => {
    const handleRouteChange = () => {
      const { query: parameters } = router;

      if (!parameters.query && searchOpen) {
        dispatch({ type: TYPE_CLOSE_SEARCH });
      }
    };

    Router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, router]);

  const ref = createRef('hub');

  const { scrollY } = useWindowScrollEffect();
  const isAtTop = scrollY === 0;
  const hasHeader = !!refs.brandHeader;
  const isHubSolid = hasHeader && !isAtTop;

  const renderNotifications = () => {
    if (isLoggedIn) {
      return [
        <NotificationsHub key="notification" />,
        <S.Separator key={uniqueId()} />,
      ];
    }

    return null;
  };

  return (
    <>
      <S.HubContainer
        ref={ref}
        solid={isHubSolid}
        aria-label="Menu om te navigeren tussen programma's en om in te loggen."
      >
        <BaseHub
          actions={[
            <Link
              href="/sluit-je-aan"
              onClick={() => TrackEvent('hub', 'click', 'sluit-je-aan')}
              key="lidworden"
              className="ABtestWordLidKnop"
              disableNextLink
            >
              <S.MemberContainer className="ABtestWordLidKnop">
                <MemberHeart />
                <span>Sluit je aan!</span>
              </S.MemberContainer>
            </Link>,
            <S.Separator key={uniqueId()} />,
            <SearchIcon key="search" />,
            <S.Separator key={uniqueId()} />,
            renderNotifications(),
            <AccountHub
              key="user"
              closeHandler={closeHandler}
              LinkComponent={NextLink}
              onClick={() => dispatch({ type: TYPE_TOGGLE_ACCOUNT_HUB })}
              isOpen={accountHubOpen}
            />,
            <S.Separator key={uniqueId()} />,
            <a
              href="https://www.npostart.nl"
              key="npo"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => TrackEvent('hub', 'click', 'npo-logo')}
            >
              <S.NPOIcon />
            </a>,
          ]}
        >
          <S.LogoLink
            href="https://www.bnnvara.nl"
            title="bnnvara home"
            onClick={() => TrackEvent('hub', 'click', 'bnnvara-logo')}
          >
            <Logo />
          </S.LogoLink>

          <S.Separator key={uniqueId()} />
          <MenuButton />
        </BaseHub>
      </S.HubContainer>
    </>
  );
};

export default Hub;
